import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PlusCircle, PencilLine, Radar, Eye, CheckCircle2, Circle, ArrowRight, Wrench, Building2, MoreHorizontal, Trash2, Lock, Unlock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { OpportunityForm } from '@/components/forms/OpportunityForm';
import { EmptyState, RecentBadge } from '@/components/common/Primitives';
import { useData } from '@/hooks/useData';
import { opportunityService } from '@/services/api';
import { contactService } from '@/services/contacts';
import { setOpportunityStatus } from '@/services/opportunityStatus';
import { formatDate, isRecent } from '@/lib/format';
import { OPP_TYPE_BY_ID } from '@/data/taxonomy';
import { useSeo } from '@/lib/seo';
import { cn } from '@/lib/utils';

export const STATUS = {
  ativa: { label: 'Ativa', cls: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200' },
  em_analise: { label: 'Em análise', cls: 'border-amber-300/25 bg-amber-300/10 text-amber-200' },
  encerrada: { label: 'Encerrada', cls: 'border-white/10 bg-white/5 text-muted-foreground' },
  rejeitada: { label: 'Rejeitada', cls: 'border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.1)] text-[rgb(255,150,165)]' },
  suspensa: { label: 'Suspensa', cls: 'border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.1)] text-[rgb(255,150,165)]' },
  aberta: { label: 'Aberta', cls: 'border-amber-300/25 bg-amber-300/10 text-amber-200' },
  procedente: { label: 'Procedente', cls: 'border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.1)] text-[rgb(255,150,165)]' },
  improcedente: { label: 'Improcedente', cls: 'border-white/10 bg-white/5 text-muted-foreground' },
};
export const StatusBadge = ({ status }) => <span className={cn('inline-flex rounded-md border px-1.5 py-0.5 text-[11px] font-medium', STATUS[status]?.cls)} data-testid={`status-${status}`}>{STATUS[status]?.label || status}</span>;

export const PageTitle = ({ title, text, right }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      {text && <p className="mt-1 text-sm text-muted-foreground">{text}</p>}
    </div>
    {right}
  </div>
);

const useMine = () => {
  const d = useData();
  const mine = useMemo(() => d.opportunities.filter((o) => o.companyId === d.myCompany?.id).sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)), [d.opportunities, d.myCompany]);
  return { ...d, mine };
};

const TONES = {
  green: 'border-emerald-400/25 bg-emerald-400/5 text-emerald-200',
  amber: 'border-amber-300/25 bg-amber-300/5 text-amber-200',
  gray: 'border-white/10 bg-white/[0.03] text-muted-foreground',
  red: 'border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.06)] text-[rgb(255,150,165)]',
};

const RECEIVED_TILES = [
  { key: 'ativa', label: 'Ativas (acesso liberado)', hint: 'Podem ver meus contatos', tone: 'green' },
  { key: 'pendente', label: 'Em análise (pendentes)', hint: 'Aguardando minha decisão', tone: 'amber' },
  { key: 'encerrada', label: 'Encerradas', hint: 'Serviços prestados e concluídos', tone: 'gray' },
  { key: 'rejeitada', label: 'Rejeitadas / revogadas', hint: 'Pedidos recusados ou acessos revogados', tone: 'red' },
];

const SENT_TILES = [
  { key: 'ativa', label: 'Aceitas (contato liberado)', hint: 'Liberaram meus contatos', tone: 'green' },
  { key: 'pendente', label: 'Em análise', hint: 'Aguardando a resposta da empresa', tone: 'amber' },
  { key: 'encerrada', label: 'Encerradas', hint: 'Serviços concluídos', tone: 'gray' },
  { key: 'rejeitada', label: 'Rejeitadas', hint: 'Empresas que recusaram o pedido', tone: 'red' },
];

function ContactPanel({ title, text, tiles, items, side, testId, inboxPath }) {
  const [open, setOpen] = useState(null);
  const count = (s) => items.filter((i) => i.status === s).length;
  const list = open ? items.filter((i) => i.status === open) : [];
  const other = (i) => (side === 'received' ? i.requesterCompany : i.targetCompany);

  return (
    <section className="surface p-5" data-testid={testId}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-base font-semibold">{title}</h2>
          <p className="mt-1 text-xs text-muted-foreground">{text}</p>
        </div>
        <Button asChild variant="secondary" size="sm" className="border border-white/10">
          <Link to={inboxPath}>Abrir caixa de entrada <ArrowRight /></Link>
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setOpen(open === t.key ? null : t.key)}
            className={cn('rounded-lg border px-3 py-3 text-left transition-colors hover:brightness-110', TONES[t.tone], open === t.key && 'ring-1 ring-white/25')}
            data-testid={`${testId}-${t.key}`}
          >
            <div className="text-[11px] font-medium">{t.label}</div>
            <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-foreground">{count(t.key)}</div>
            <div className="mt-1 text-[10px] text-muted-foreground">{t.hint}</div>
          </button>
        ))}
      </div>
      {open && (
        <div className="mt-4 divide-y divide-white/5 rounded-lg border border-white/10" data-testid={`${testId}-list`}>
          {list.length ? list.map((i) => {
            const c = other(i);
            return (
              <div key={i.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  {c ? (
                    <Link to={`/empresas/${c.id}`} className="truncate text-sm font-medium hover:text-primary">{c.name}</Link>
                  ) : (
                    <span className="text-sm text-muted-foreground">Empresa indisponível</span>
                  )}
                  <div className="text-[11px] text-muted-foreground">
                    {c ? `${c.city} · ${c.uf} · ` : ''}{formatDate(i.createdAt)}
                  </div>
                </div>
                {i.status === 'pendente' && side === 'received' && (
                  <Link to={inboxPath} className="text-xs text-primary hover:underline">Decidir</Link>
                )}
              </div>
            );
          }) : (
            <div className="px-3 py-4 text-sm text-muted-foreground">Nenhuma empresa nesta categoria.</div>
          )}
        </div>
      )}
    </section>
  );
}

export function Overview() {
  useSeo({ title: 'Painel' });
  const { myCompany: c, mine, user } = useMine();
  const [toDelete, setToDelete] = useState(null);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);

  useEffect(() => {
    if (!c?.id) return;
    Promise.all([contactService.received(c.id), contactService.sent(c.id)])
      .then(([r, s]) => {
        setReceived(r);
        setSent(s);
      })
      .catch((e) => toast.error(e.message));
  }, [c?.id]);

  const removeOpp = async () => {
    try {
      await opportunityService.remove(toDelete.id);
      toast.success('Publicação removida');
      setToDelete(null);
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!c) {
    return (
      <div className="surface mx-auto mt-10 max-w-lg p-8 text-center" data-testid="overview-no-company">
        <h2 className="text-lg font-semibold">Carregando sua empresa…</h2>
        <p className="mt-2 text-sm text-muted-foreground">Se esta mensagem não sumir, aperte F5 na página.</p>
      </div>
    );
  }

  const checklist = [
    { ok: !!c.tagline && !!c.description && c.description.length >= 40, label: 'Descrição completa', to: '/painel/perfil' },
    { ok: (c.services || []).length >= 2, label: 'Ao menos 2 serviços cadastrados', to: '/painel/servicos' },
    { ok: !!c.website || !!c.whatsapp || !!c.email, label: 'Canal oficial de contato', to: '/painel/perfil' },
    { ok: (c.portfolio || []).length > 0, label: 'Item de portfólio', to: '/painel/servicos' },
    { ok: c.cnpjStatus === 'valido', label: 'CNPJ validado na Receita', to: '/painel/perfil' },
    { ok: mine.length > 0, label: 'Primeira oportunidade publicada', to: '/painel/publicar' },
  ];
  const pct = Math.round((checklist.filter((x) => x.ok).length / checklist.length) * 100);

  return (
    <div data-testid="dashboard-overview">
      <PageTitle
        title={`Olá, ${user.name.split(' ')[0]}`}
        text="Resumo da sua presença na rede e solicitações de contato."
        right={<Button asChild className="btn-glow" data-testid="overview-publish-button"><Link to="/painel/publicar"><PlusCircle /> Publicar oportunidade</Link></Button>}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ContactPanel
            testId="received-panel"
            title="Gestão de privacidade · pedidos que recebi"
            text="Empresas que pediram para ver meus contatos."
            tiles={RECEIVED_TILES}
            items={received}
            side="received"
            inboxPath="/painel/mensagens"
          />
        </div>
        <section className="surface p-5" data-testid="overview-checklist">
          <div className="flex items-center justify-between"><h2 className="font-display text-base font-semibold">Complete seu perfil</h2><span className="font-mono text-sm text-primary">{pct}%</span></div>
          <Progress value={pct} className="mt-3 h-1.5 bg-white/5" />
          <ul className="mt-4 space-y-1">
            {checklist.map((i) => (
              <li key={i.label}>
                <Link to={i.to} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-white/5">
                  {i.ok ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                  <span className={cn('flex-1', i.ok && 'text-muted-foreground line-through decoration-white/20')}>{i.label}</span>
                  {!i.ok && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ContactPanel
            testId="sent-panel"
            title="Solicitações que enviei"
            text="Empresas para as quais pedi acesso aos contatos."
            tiles={SENT_TILES}
            items={sent}
            side="sent"
            inboxPath="/painel/mensagens"
          />
        </div>
        <section className="surface p-5" data-testid="overview-shortcuts">
          <h2 className="mb-3 font-display text-base font-semibold">Atalhos</h2>
          <div className="grid gap-2">
            {[
              { to: '/painel/publicar', icon: PlusCircle, l: 'Publicar oportunidade' },
              { to: '/painel/perfil', icon: Building2, l: 'Editar perfil' },
              { to: '/painel/servicos', icon: Wrench, l: 'Gerenciar serviços' },
              { to: `/empresas/${c.id}`, icon: Eye, l: 'Ver perfil público' },
            ].map(({ to, icon: Icon, l }) => (
              <Link key={to} to={to} className="flex items-center gap-3 rounded-md border border-white/10 bg-[#0b0f16] px-3 py-2.5 text-sm transition-colors hover:border-cyan-300/30"><Icon className="h-4 w-4 text-primary" />{l}<ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" /></Link>
            ))}
          </div>
        </section>
      </div>

      <section className="surface mt-4 p-5" data-testid="overview-active-publications">
        <div className="mb-3 flex items-center justify-between"><h2 className="font-display text-base font-semibold">Publicações recentes</h2><Button asChild variant="ghost" size="sm"><Link to="/painel/publicacoes">Gerenciar <ArrowRight /></Link></Button></div>
        {mine.length ? (
          <ul className="divide-y divide-white/5">
            {mine.slice(0, 5).map((o) => (
              <li key={o.id} className="flex flex-wrap items-center gap-3 py-2.5" data-testid="overview-publication-item">
                <StatusBadge status={o.status} />
                <Link to={`/oportunidades/${o.id}`} className="min-w-0 flex-1 truncate text-sm hover:text-primary">{o.title}</Link>
                {isRecent(o.publishedAt) && <RecentBadge />}
                <span className="font-mono text-[11px] text-muted-foreground">{formatDate(o.publishedAt)}</span>
                <div className="flex items-center gap-1">
                  <Button asChild variant="ghost" size="icon" className="h-8 w-8" aria-label="Editar publicação" title="Editar" data-testid={`overview-edit-${o.id}`}>
                    <Link to={`/painel/publicacoes/${o.id}/editar`}><PencilLine className="h-4 w-4" /></Link>
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[#ff6b84]" aria-label="Remover publicação" title="Remover" onClick={() => setToDelete(o)} data-testid={`overview-delete-${o.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : <EmptyState icon={Radar} title="Nenhuma publicação ainda" text="Publique sua primeira oportunidade para aparecer no radar." />}
      </section>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0b0f16]">
          <AlertDialogHeader><AlertDialogTitle>Remover publicação?</AlertDialogTitle><AlertDialogDescription>“{toDelete?.title}” será removida. Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={removeOpp} data-testid="overview-delete-confirm">Remover</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function MyOpportunities() {
  useSeo({ title: 'Minhas oportunidades' });
  const { mine } = useMine();
  const [tab, setTab] = useState('todas');
  const list = tab === 'todas' ? mine : mine.filter((o) => o.status === tab);
  return (
    <div data-testid="my-opportunities">
      <PageTitle title="Minhas oportunidades" text="Acompanhe o status de cada publicação." right={<Button asChild className="btn-glow"><Link to="/painel/publicar"><PlusCircle /> Nova</Link></Button>} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap border border-white/10 bg-[#0b0f16]">
          {[['todas', 'Todas'], ['ativa', 'Ativas'], ['em_analise', 'Em análise'], ['encerrada', 'Encerradas'], ['rejeitada', 'Rejeitadas']].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`my-opps-tab-${v}`}>{l} <span className="ml-1.5 font-mono text-[11px] opacity-70">{v === 'todas' ? mine.length : mine.filter((o) => o.status === v).length}</span></TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="mt-4 grid gap-3">
        {list.length ? list.map((o) => (
          <div key={o.id} className="surface flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2"><StatusBadge status={o.status} />{isRecent(o.publishedAt) && <RecentBadge />}<span className="text-xs text-muted-foreground">{OPP_TYPE_BY_ID[o.type]?.short}</span></div>
              <div className="mt-1.5 truncate text-sm font-medium">{o.title}</div>
              <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">Publicada {formatDate(o.publishedAt)} {o.deadline && `· prazo ${formatDate(o.deadline)}`}</div>
              {o.moderationFlags?.length > 0 && o.status === 'em_analise' && <div className="mt-1 text-[11px] text-amber-200">Verificação automática: {o.moderationFlags.join(', ')}</div>}
            </div>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="secondary" className="border border-white/10"><Link to={`/oportunidades/${o.id}`}><Eye /> Ver</Link></Button>
              <Button asChild size="sm" variant="secondary" className="border border-white/10"><Link to={`/painel/publicacoes/${o.id}/editar`}><PencilLine /> Editar</Link></Button>
            </div>
          </div>
        )) : <EmptyState icon={Radar} title="Nada por aqui" text="Nenhuma publicação com este status." testId="my-opps-empty" />}
      </div>
    </div>
  );
}

export function Publish() {
  useSeo({ title: 'Publicar oportunidade' });
  const { myCompany } = useData();
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl" data-testid="publish-page">
      <PageTitle title="Publicar oportunidade" text="Descreva o que sua empresa procura ou oferece." />
      <div className="surface p-5 sm:p-6"><OpportunityForm company={myCompany} onSaved={(o) => navigate(`/oportunidades/${o.id}`)} /></div>
    </div>
  );
}

export function EditOpportunity() {
  const { id } = useParams();
  const { opportunities, myCompany } = useData();
  const navigate = useNavigate();
  const o = opportunities.find((x) => x.id === id && x.companyId === myCompany?.id);
  useSeo({ title: 'Editar oportunidade' });
  if (!o) return <EmptyState icon={Radar} title="Publicação não encontrada" text="Ela pode ter sido removida ou não pertence à sua empresa." action="Voltar" onAction={() => navigate('/painel/publicacoes')} testId="edit-opp-not-found" />;
  return (
    <div className="max-w-3xl" data-testid="edit-opportunity-page">
      <PageTitle title="Editar oportunidade" text={o.title} />
      <div className="surface p-5 sm:p-6"><OpportunityForm key={o.id} company={myCompany} opportunity={o} onSaved={() => navigate('/painel/publicacoes')} /></div>
    </div>
  );
}

export function EditPublications() {
  useSeo({ title: 'Editar publicações' });
  const { mine } = useMine();
  const navigate = useNavigate();
  const [toDelete, setToDelete] = useState(null);
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); } catch (e) { toast.error(e.message); }
  };
  return (
    <div data-testid="edit-publications">
      <PageTitle title="Editar publicações" text="Edite, encerre, reabra ou remova suas oportunidades." right={<Button asChild className="btn-glow"><Link to="/painel/publicar"><PlusCircle /> Nova</Link></Button>} />
      {mine.length ? (
        <div className="surface overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="publications-table">
              <TableHeader><TableRow className="border-white/10 hover:bg-transparent"><TableHead>Título</TableHead><TableHead>Status</TableHead><TableHead className="hidden md:table-cell">Publicada</TableHead><TableHead className="hidden md:table-cell">Prazo</TableHead><TableHead className="w-12" /></TableRow></TableHeader>
              <TableBody>
                {mine.map((o) => (
                  <TableRow key={o.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`publication-row-${o.id}`}>
                    <TableCell className="max-w-[320px]"><div className="truncate font-medium">{o.title}</div><div className="text-[11px] text-muted-foreground">{OPP_TYPE_BY_ID[o.type]?.short}</div></TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="hidden font-mono text-xs md:table-cell">{formatDate(o.publishedAt)}</TableCell>
                    <TableCell className="hidden font-mono text-xs md:table-cell">{o.deadline ? formatDate(o.deadline) : '—'}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label="Ações" data-testid={`publication-actions-${o.id}`}><MoreHorizontal /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="border-white/10 bg-[#0b0f16]">
                          <DropdownMenuItem onClick={() => navigate(`/painel/publicacoes/${o.id}/editar`)} data-testid={`publication-edit-${o.id}`}><PencilLine /> Editar</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/oportunidades/${o.id}`)}><ExternalLink /> Ver página</DropdownMenuItem>
                          {o.status === 'ativa' && <DropdownMenuItem onClick={() => act(() => setOpportunityStatus(o.id, 'encerrada'), 'Oportunidade encerrada')} data-testid={`publication-close-${o.id}`}><Lock /> Encerrar</DropdownMenuItem>}
                          {o.status === 'encerrada' && <DropdownMenuItem onClick={() => act(() => setOpportunityStatus(o.id, 'ativa'), 'Oportunidade reaberta')} data-testid={`publication-reopen-${o.id}`}><Unlock /> Reabrir</DropdownMenuItem>}
                          <DropdownMenuSeparator className="bg-white/10" />
                          <DropdownMenuItem className="text-[rgb(255,130,150)] focus:text-[rgb(255,130,150)]" onClick={() => setToDelete(o)} data-testid={`publication-delete-${o.id}`}><Trash2 /> Remover</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : <EmptyState icon={Radar} title="Nenhuma publicação" text="Você ainda não publicou oportunidades." action="Publicar agora" onAction={() => navigate('/painel/publicar')} />}
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0b0f16]">
          <AlertDialogHeader><AlertDialogTitle>Remover publicação?</AlertDialogTitle><AlertDialogDescription>“{toDelete?.title}” será removida. Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => act(() => opportunityService.remove(toDelete.id), 'Publicação removida')} data-testid="publication-delete-confirm">Remover</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}